import { useState } from "react";
import { format } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Search,
  Filter,
  History,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useStockHistory } from "@/hooks/useStockTransactions";
import { useItems } from "@/hooks/useItems";
import { useLocation } from "wouter";
import LoadingIndicator from "@/components/LoadingIndicator";
import type { TransactionType } from "@/types/stockTran";
import { useDecimalPrecision } from "@/hooks/useDecimalPrecision";

export const StockHistory = () => {
  const { format: formatDecimal } = useDecimalPrecision();
  const [_, navigate] = useLocation();
  const { items, isLoading: isLoadingItems } = useItems();

  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  const { data, isLoading } = useStockHistory(
    selectedItemId || undefined,
    dateFrom || dateTo ? { dateFrom, dateTo } : undefined
  );

  const selectedItem = items?.data?.find((item) => item.id === selectedItemId);

  const getTypeColor = (type: TransactionType) => {
    switch (type) {
      case "GI":
        return "bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700";
      case "GO":
        return "bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700";
      case "INV":
        return "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700";
      case "DO":
        return "bg-orange-100 text-orange-800 border-orange-300 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600";
    }
  };

  const getTypeLabel = (type: TransactionType) => {
    switch (type) {
      case "GI":
        return "Stock In";
      case "GO":
        return "Stock Out";
      case "INV":
        return "Invoice (OUT)";
      case "DO":
        return "Delivery Order";
      default:
        return type;
    }
  };

  const getTypeIcon = (type: TransactionType) => {
    switch (type) {
      case "GI":
        return <ArrowDownToLine className="h-3 w-3" />;
      case "GO":
      case "INV":
      case "DO":
        return <ArrowUpFromLine className="h-3 w-3" />;
      default:
        return null;
    }
  };

  const handleResetFilters = () => {
    setSelectedItemId("");
    setDateFrom("");
    setDateTo("");
    setItemSearch("");
    setShowDropdown(false);
  };

  const filteredItems = items?.data?.filter((item) => {
    const searchLower = itemSearch.toLowerCase();
    return (
      item.stockType === "stock" &&
      (item.itemCode.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower))
    );
  });

  if (isLoadingItems) return <LoadingIndicator />;

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Stock History</h1>
        <Button variant="outline" onClick={() => navigate("/stock-transactions")}>
          Back to List
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              <span className="font-semibold">Filters</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="itemSearch">
                  Select Item <span className="text-red-600">*</span>
                </Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                  <Input
                    placeholder="Search by item code or description..."
                    value={itemSearch}
                    onChange={(e) => {
                      setItemSearch(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    className="pl-8"
                  />
                </div>
                {showDropdown && itemSearch && filteredItems && filteredItems.length > 0 && (
                  <div className="border rounded-md mt-2 max-h-48 overflow-y-auto absolute z-20 bg-white dark:bg-gray-900 dark:border-gray-700 w-full md:w-[calc(33.333%-1rem)] shadow-lg">
                    {filteredItems.slice(0, 10).map((item) => (
                      <div
                        key={item.id}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                        onClick={() => {
                          setSelectedItemId(item.id);
                          setItemSearch(`${item.itemCode} - ${item.description}`);
                          setShowDropdown(false);
                        }}
                      >
                        <div className="font-medium">{item.itemCode}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {item.description}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-500">
                          Current Stock: {item.quantity || 0} | Avg Cost: RM{" "}
                          {formatDecimal(item.weightedAverage) || "0.00"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="dateFrom">Date From</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="dateTo">Date To</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={handleResetFilters}>
                Reset Filters
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selected Item Summary */}
      {selectedItem && (
        <Card className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Item Code</div>
                <div className="text-lg font-bold">{selectedItem.itemCode}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Description</div>
                <div className="text-lg font-semibold">
                  {selectedItem.description}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Current Stock</div>
                <div className="text-lg font-bold text-blue-600">
                  {selectedItem.quantity || 0}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Weighted Average Cost</div>
                <div className="text-lg font-bold text-green-600">
                  RM {formatDecimal(selectedItem.weightedAverage) || "0.00"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History Table */}
      <Card>
        <CardContent className="pt-6">
          {!selectedItemId ? (
            <div className="text-center py-12 text-muted-foreground">
              <div className="mb-4">
                <History className="h-12 w-12 mx-auto text-gray-300" />
              </div>
              <p className="text-lg font-medium">No Item Selected</p>
              <p className="text-sm mt-2">
                Please search and select an item to view its stock history
              </p>
            </div>
          ) : isLoading ? (
            <LoadingIndicator />
          ) : data?.data && data.data.length > 0 ? (
            <Table>
              <TableHeader className="bg-slate-50 dark:bg-slate-800">
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Voucher</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Cost (RM)</TableHead>
                  <TableHead className="text-right">Balance Qty</TableHead>
                  <TableHead className="text-right">Weighted Avg</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((record, index) => {
                  const isIncrease = record.type === "GI";
                  const prevRecord = index > 0 ? data.data[index - 1] : null;

                  return (
                    <TableRow key={record.id}>
                      <TableCell>
                        <div className="text-sm">
                          {format(new Date(record.entryDate), "dd MMM yyyy")}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {record.entryTime}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {record.voucherNumber}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${getTypeColor(record.type)} flex items-center gap-1 w-fit`}
                          variant="outline"
                        >
                          {getTypeIcon(record.type)}
                          {getTypeLabel(record.type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={`font-medium ${
                            isIncrease ? "text-green-600" : "text-red-600"
                          }`}
                        >
                          {isIncrease ? "+" : "-"}
                          {record.quantity}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatDecimal((
                          record.baseCurrencyCostIn ||
                          record.baseCurrencyCostOut ||
                          0
                        ))}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <span className="font-semibold">
                            {formatDecimal(record.upToBalance)}
                          </span>
                          {prevRecord && (
                            <span className="text-xs text-gray-500">
                              {isIncrease ? (
                                <TrendingUp className="h-3 w-3 text-green-600" />
                              ) : (
                                <TrendingDown className="h-3 w-3 text-red-600" />
                              )}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatDecimal(record.baseCurrencyCostIn) || "-"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-400">
                        {record.reference || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <div className="mb-4">
                <Search className="h-12 w-12 mx-auto text-gray-300" />
              </div>
              <p className="text-lg font-medium">No History Found</p>
              <p className="text-sm mt-2">
                {dateFrom || dateTo
                  ? "No transactions found for the selected date range"
                  : "This item has no stock transactions yet"}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
